import type { Camp } from '@orbomb/content';
import type { SimConfig } from '../config';
import { CAMP_ORDER, opponentOf } from '../constants';
import type { EventSink } from '../events';
import type { EndCause, MatchResult, MatchState, TieBreak } from '../types';

/** Start-of-tick transitions: clock phases and player state timers. */
export function beginTick(state: MatchState, config: SimConfig, sink: EventSink) {
  const tick = sink.tick;
  const clock = state.clock;
  if (clock.phase === 'COUNTDOWN' && tick >= clock.countdownEndTick) {
    clock.phase = 'REGULATION';
    sink.emit({ type: 'REGULATION_STARTED' });
  }
  if (clock.phase === 'REGULATION' && tick >= clock.regulationEndTick) {
    clock.phase = 'OVERTIME';
    sink.emit({ type: 'OVERTIME_STARTED' });
  }
  if (clock.phase === 'SUDDEN_DEATH' && clock.suddenDeathStartTick !== null) {
    const elapsed = tick - clock.suddenDeathStartTick;
    if (elapsed === config.ticks.suddenDeathAutoArmAfter) {
      clock.nextAutoArmTick = tick;
      sink.emit({ type: 'SUDDEN_DEATH_ESCALATION', level: 'AUTO_ARM' });
    }
    if (elapsed === config.ticks.suddenDeathUnlimitedRangeAfter) {
      sink.emit({ type: 'SUDDEN_DEATH_ESCALATION', level: 'UNLIMITED_RANGE' });
    }
  }
  for (const player of state.players) {
    if (player.status === 'STUNNED' && tick >= player.stunUntilTick) {
      player.status = 'PROTECTED';
      sink.emit({
        type: 'PLAYER_WOKE',
        camp: player.camp,
        protectedUntilTick: player.protectedUntilTick,
      });
    }
    if (player.status === 'PROTECTED' && tick >= player.protectedUntilTick) {
      player.status = 'NORMAL';
      sink.emit({ type: 'PLAYER_PROTECTION_ENDED', camp: player.camp });
    }
  }
}

function remainingLives(state: MatchState): Record<Camp, number> {
  return { ORANGE: state.totems[0].hp, TURQUOISE: state.totems[1].hp };
}

function finish(
  state: MatchState,
  winner: Camp,
  cause: EndCause,
  tieBreak: TieBreak,
  sink: EventSink,
) {
  const lives = remainingLives(state);
  const result: MatchResult = {
    winner,
    cause,
    tieBreak,
    tick: sink.tick,
    score: { ...lives },
    totemHp: { ...lives },
  };
  state.result = result;
  state.clock.phase = 'ENDED';
  sink.emit({ type: 'MATCH_ENDED', result });
}

function higherLives(state: MatchState): Camp | null {
  const { ORANGE, TURQUOISE } = remainingLives(state);
  if (ORANGE === TURQUOISE) return null;
  return ORANGE > TURQUOISE ? 'ORANGE' : 'TURQUOISE';
}

/**
 * Both totems receive one life. The sudden-death clock starts only on first entry,
 * so a simultaneous double hit does not reset anti-stall escalation.
 */
export function enterSuddenDeath(state: MatchState, sink: EventSink) {
  for (const totem of state.totems) {
    totem.hp = 1;
    totem.destroyedAtTick = null;
  }
  const clock = state.clock;
  if (clock.phase === 'SUDDEN_DEATH') return;
  clock.phase = 'SUDDEN_DEATH';
  clock.suddenDeathStartTick = sink.tick + 1;
  clock.nextAutoArmTick = null;
  sink.emit({ type: 'SUDDEN_DEATH_STARTED' });
}

/** True when nothing dangerous remains after this tick. */
export function overtimeSettled(state: MatchState, nextTick: number): boolean {
  return (
    !state.goldens.some((g) => g.status === 'ARMED') &&
    !state.explosions.some((e) => e.lethalUntilTick > nextTick)
  );
}

/**
 * End-of-match evaluation, run only after every event of the tick has been applied.
 * Order: single destruction, double destruction → sudden death,
 * overtime remaining lives.
 */
export function evaluateEnd(state: MatchState, _config: SimConfig, sink: EventSink) {
  const destroyed = CAMP_ORDER.filter((camp) => state.totems[camp === 'ORANGE' ? 0 : 1].hp <= 0);
  if (destroyed.length === 2) {
    enterSuddenDeath(state, sink);
    return;
  }
  if (destroyed.length === 1) {
    const cause: EndCause =
      state.clock.phase === 'SUDDEN_DEATH' ? 'SUDDEN_DEATH_TOTEM' : 'TOTEM_DESTROYED';
    finish(state, opponentOf(destroyed[0] as Camp), cause, 'NONE', sink);
    return;
  }
  const clock = state.clock;
  if (clock.phase === 'OVERTIME' && overtimeSettled(state, sink.tick + 1)) {
    const leader = higherLives(state);
    if (leader) {
      finish(state, leader, 'LIVES_AFTER_OVERTIME', 'NONE', sink);
    } else {
      enterSuddenDeath(state, sink);
    }
  }
}

/** Disconnection beyond the reconnection window. */
export function applyForfeit(state: MatchState, loser: Camp, sink: EventSink) {
  if (state.result) return;
  finish(state, opponentOf(loser), 'FORFEIT', 'NONE', sink);
}
